import { useCallback, useEffect, useMemo, useState } from 'react'
import Notiflix from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { FaTrash } from 'react-icons/fa'
import api from '../../../../../../helpers/apiRequester'
import {
    Banner,
    Button,
    Card,
    Checkbox,
    Column,
    DataTable,
    EmptyState,
    Field,
    FieldGrid,
    Input,
    KeyValueList,
    Radio,
    Spinner,
    StatusPill,
    notiflixDanger,
} from '../../../../../../ui'
import { formatDate } from '../../shared'
import styles from './styles.module.scss'

/**
 * Client Search → "Account actions" (Sentinel users only).
 *
 * One client from one introducer database (origin + clientId). Lets the
 * operator cancel some or all of the client's claims with a reason — either
 * sending the client the cancellation letter (through the action server) or in
 * the backend only — optionally carrying the cancellation over to
 * funding-monitor; reactivate cancelled claims (optionally emailing the client
 * that the claim has resumed); resend a cancellation letter; and, for the
 * introducers whose schema the deletion tool understands, run a full erasure.
 *
 * Which introducers support cancellation comes from the API (every registry
 * entry with a cancellation adapter, so ZappedCases and OA included).
 */

interface ClaimState {
    case_ref: string
    lender: string | null
    policy_number: string | null
    start_date: string | null
    status?: string | null
    has_state: boolean
    cancelled: boolean
    cancellation_date: string | null
    cancellation_reason: string | null
}

interface ActionCapabilities {
    cancel: boolean
    cancelWithLetter: boolean
    reactivate: boolean
    delete: boolean
    funding: boolean
}

type CancelMode = 'letter' | 'silent'

interface PerClaimResult {
    case_ref: string
    ok: boolean
    code?: string
    error?: string
    letter_sent?: boolean
    letter_error?: string | null
    email_sent?: boolean
    email_error?: string | null
}

interface ReactivateResponse {
    success: boolean
    message: string
    sendEmail: boolean
    reactivated: string[]
    failed: PerClaimResult[]
    emailsSent: number
    emailFailures: { case_ref: string; error: string | null }[]
    claims: ClaimState[]
}

interface FundingReference {
    funder_client_id: number
    funder_case_ref: string
    introducer_case_ref: string | null
    introducer: string
    introducer_client_id: number | null
    batch_group: string | null
    nera_id: string | null
    batch_processed: string | null
    audit_removed: boolean
    audit_removed_reason: string | null
    cancelled: boolean
    cancelled_date: string | null
    cancelled_reason: string | null
    client_status: string | null
    closed: string | number | null
    email_address: string | null
    direct_match: boolean
}

interface OtherIntroducerMatch {
    introducer: string
    label: string
    clients: number
    error?: boolean
}

interface ActionsSummary {
    success: boolean
    supported: boolean
    message?: string
    introducer: string
    introducerLabel: string
    schemaFamily?: 'leads' | 'matters' | 'oa'
    capabilities?: ActionCapabilities
    client: { id: number; email: string; clientRef: string | null; name: string | null }
    claims: ClaimState[]
    funding: { available: boolean; references: FundingReference[]; error?: string }
    otherIntroducers: OtherIntroducerMatch[]
}

interface CancelResponse {
    success: boolean
    message: string
    sendEmail?: boolean
    updated: number
    cancelled: string[]
    failed?: PerClaimResult[]
    alreadyCancelled: string[]
    withoutState: string[]
    cancellationDate: string
    funding: { references: number; claims: number; error?: string } | null
    claims: ClaimState[]
    fundingReferences: FundingReference[]
    lettersSent?: number | boolean
    letterFailures?: { case_ref: string; error: string | null }[]
}

/** Reply from /actions/cancel (the action server, via the API) for a single claim. */
interface SingleActionResponse {
    success: boolean
    code?: string
    message?: string
    letter_sent?: boolean
    recipient?: string | null
}

const DEFAULT_CAPABILITIES: ActionCapabilities = {
    cancel: true,
    cancelWithLetter: true,
    reactivate: true,
    delete: true,
    funding: true,
}

interface IntroducerDeletionSummary {
    introducer: string
    label: string
    clients: number
    cases: number
    tableCounts: Record<string, number>
    s3PrefixCount: number
    s3KeyCount: number
}

interface DeletePreview {
    success: boolean
    preview: boolean
    message?: string
    allIntroducers: boolean
    includeFunding: boolean
    introducers: IntroducerDeletionSummary[]
    totalClients: number
    totalCases: number
    funding: {
        counts: { funder_clients: number; funder_references: number; funder_claims: number }
        batchGroups: string[]
        skipped?: boolean
    }
    s3?: { deleted: number; failed: number }
    fundingDeleted?: { funder_clients: number; funder_references: number; funder_claims: number } | null
}

export interface ClientActionsTabProps {
    clientId: number
    origin: string
    clientEmail?: string
    clientName?: string
    /** Called with the refreshed claim states after a cancellation so the parent can update its Claims panel. */
    onClaimsCancelled?: (claims: ClaimState[]) => void
    /** Called once the client has been erased so the parent can drop it from the results. */
    onClientDeleted?: () => void
}

const DEFAULT_REASON = 'By client request'
const DELETE_CONFIRM_WORD = 'DELETE'

const errorMessage = (error: any, fallback: string) => error?.response?.data?.message || error?.message || fallback

const plural = (n: number, word: string, pluralWord = `${word}s`) => `${n} ${n === 1 ? word : pluralWord}`

const fundingStatus = (ref: FundingReference): { label: string; tone: 'good' | 'bad' | 'warn' } => {
    if (ref.cancelled) return { label: 'Cancelled', tone: 'bad' }
    if (ref.audit_removed) return { label: 'Audit removed', tone: 'warn' }
    return { label: 'Active', tone: 'good' }
}

export const ClientActionsTab = ({
    clientId,
    origin,
    clientEmail,
    clientName,
    onClaimsCancelled,
    onClientDeleted,
}: ClientActionsTabProps) => {
    const [summary, setSummary] = useState<ActionsSummary | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)

    // Cancellation
    const [selectedRefs, setSelectedRefs] = useState<Set<string>>(new Set())
    const [reason, setReason] = useState(DEFAULT_REASON)
    const [cancelMode, setCancelMode] = useState<CancelMode>('silent')
    const [cancelInFunding, setCancelInFunding] = useState(true)
    const [isCancelling, setIsCancelling] = useState(false)
    const [cancelResult, setCancelResult] = useState<CancelResponse | null>(null)

    // Reactivation / resend (per claim)
    const [resumedEmail, setResumedEmail] = useState(false)
    const [busyClaim, setBusyClaim] = useState<{ caseRef: string; action: 'reactivate' | 'resend' } | null>(null)
    const [reactivateResult, setReactivateResult] = useState<string | null>(null)

    // Deletion
    const [deleteAllIntroducers, setDeleteAllIntroducers] = useState(false)
    const [deleteFunding, setDeleteFunding] = useState(true)
    const [deletePreview, setDeletePreview] = useState<DeletePreview | null>(null)
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [confirmText, setConfirmText] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)

    const loadSummary = useCallback(async () => {
        setIsLoading(true)
        setLoadError(null)
        try {
            const { data } = await api<ActionsSummary>({
                url: '/client-search-actions-summary',
                data: { introducer: origin, clientId },
            })
            if (!data?.success) {
                setLoadError(data?.message || 'Failed to load client actions')
                return
            }
            setSummary(data)
        } catch (error: any) {
            setLoadError(errorMessage(error, 'Failed to load client actions'))
        } finally {
            setIsLoading(false)
        }
    }, [origin, clientId])

    useEffect(() => {
        loadSummary()
    }, [loadSummary])

    const claims = useMemo(() => summary?.claims ?? [], [summary])
    const capabilities = summary?.capabilities ?? DEFAULT_CAPABILITIES
    const fundingRefs = useMemo(() => summary?.funding?.references ?? [], [summary])
    const activeClaims = useMemo(() => claims.filter((c) => !c.cancelled), [claims])
    const cancelledClaims = useMemo(() => claims.filter((c) => c.cancelled), [claims])
    const sendLetter = cancelMode === 'letter' && capabilities.cancelWithLetter
    const displayEmail = summary?.client.email || clientEmail || ''
    const displayName = summary?.client.name || clientName || ''
    const fundingByCaseRef = useMemo(() => {
        const map = new Map<string, FundingReference[]>()
        for (const ref of fundingRefs) {
            if (!ref.introducer_case_ref) continue
            const list = map.get(ref.introducer_case_ref) || []
            list.push(ref)
            map.set(ref.introducer_case_ref, list)
        }
        return map
    }, [fundingRefs])
    const selectedFundingRefs = useMemo(
        () =>
            fundingRefs.filter(
                (ref) => ref.introducer_case_ref && selectedRefs.has(ref.introducer_case_ref) && !ref.cancelled,
            ).length,
        [fundingRefs, selectedRefs],
    )

    const toggleRef = (caseRef: string) => {
        setSelectedRefs((prev) => {
            const next = new Set(prev)
            if (next.has(caseRef)) next.delete(caseRef)
            else next.add(caseRef)
            return next
        })
    }
    const selectAllActive = () => setSelectedRefs(new Set(activeClaims.map((c) => c.case_ref)))
    const clearSelection = () => setSelectedRefs(new Set())

    // Any change to the deletion scope invalidates the preview + typed confirmation.
    const resetDeletePreview = () => {
        setDeletePreview(null)
        setConfirmText('')
    }

    const runCancellation = async (allClaims: boolean) => {
        const refs = allClaims ? activeClaims.map((c) => c.case_ref) : Array.from(selectedRefs)
        if (!refs.length) return
        setIsCancelling(true)
        setCancelResult(null)
        try {
            const { data } = await api<CancelResponse>({
                url: '/client-search-cancel-claims',
                data: {
                    introducer: origin,
                    clientId,
                    caseRefs: refs,
                    allClaims,
                    reason: reason.trim() || DEFAULT_REASON,
                    sendEmail: sendLetter,
                    includeFunding: cancelInFunding && fundingRefs.length > 0,
                },
            })
            // The API returns 200 with success:false when some claims failed but
            // others were cancelled; the claims list is still authoritative.
            if (data?.claims) {
                setCancelResult(data)
                setSelectedRefs(new Set())
                setSummary((prev) =>
                    prev
                        ? {
                              ...prev,
                              claims: data.claims,
                              funding: {
                                  ...prev.funding,
                                  references: data.fundingReferences ?? prev.funding.references,
                              },
                          }
                        : prev,
                )
                onClaimsCancelled?.(data.claims)
            }
            if (!data?.success) {
                Notiflix.Report.failure('Cancellation Incomplete', data?.message || 'Failed to cancel claims.', 'OK')
                return
            }
            if (data.funding?.error || (data.letterFailures?.length ?? 0) > 0) {
                Notiflix.Report.warning('Claims Cancelled', data.message, 'OK')
            } else {
                Notiflix.Report.success('Claims Cancelled', data.message, 'OK')
            }
        } catch (error: any) {
            Notiflix.Report.failure('Cancellation Failed', errorMessage(error, 'Failed to cancel claims.'), 'OK')
        } finally {
            setIsCancelling(false)
        }
    }

    const confirmCancellation = (allClaims: boolean) => {
        const count = allClaims ? activeClaims.length : selectedRefs.size
        if (!count) return
        const fundingNote =
            cancelInFunding && fundingRefs.length > 0
                ? ' Linked funding-monitor references will also be marked cancelled.'
                : ''
        const letterNote = sendLetter
            ? ` The client will be emailed the cancellation letter for each claim${
                  displayEmail ? ` (${displayEmail})` : ''
              }.`
            : ' No letters or emails will be sent.'
        Confirm.show(
            allClaims ? 'Cancel all claims?' : `Cancel ${plural(count, 'claim')}?`,
            `Reason: "${reason.trim() || DEFAULT_REASON}".${letterNote}${fundingNote}`,
            sendLetter ? 'Cancel and send letter' : 'Cancel claims',
            'Go back',
            () => void runCancellation(allClaims),
            () => undefined,
            notiflixDanger,
        )
    }

    const applyClaims = (nextClaims: ClaimState[]) => {
        setSummary((prev) => (prev ? { ...prev, claims: nextClaims } : prev))
        onClaimsCancelled?.(nextClaims)
    }

    const runReactivation = async (caseRef: string) => {
        setBusyClaim({ caseRef, action: 'reactivate' })
        setReactivateResult(null)
        try {
            const { data } = await api<ReactivateResponse>({
                url: '/client-search-reactivate-claims',
                data: { introducer: origin, clientId, caseRefs: [caseRef], sendEmail: resumedEmail },
            })
            if (data?.claims) applyClaims(data.claims)
            if (!data?.success) {
                Notiflix.Report.failure('Reactivation Failed', data?.message || 'Failed to reactivate the claim.', 'OK')
                return
            }
            setReactivateResult(data.message)
            if (data.emailFailures?.length) Notiflix.Report.warning('Claim Reactivated', data.message, 'OK')
            else Notiflix.Report.success('Claim Reactivated', data.message, 'OK')
        } catch (error: any) {
            Notiflix.Report.failure('Reactivation Failed', errorMessage(error, 'Failed to reactivate the claim.'), 'OK')
        } finally {
            setBusyClaim(null)
        }
    }

    const confirmReactivation = (claim: ClaimState) => {
        Confirm.show(
            `Reactivate ${claim.case_ref}?`,
            `The claim goes back to live in ${summary?.introducerLabel ?? origin}.` +
                (resumedEmail
                    ? ` The client will be emailed that their claim has resumed${displayEmail ? ` (${displayEmail})` : ''}.`
                    : ' No email will be sent.'),
            'Reactivate',
            'Go back',
            () => void runReactivation(claim.case_ref),
            () => undefined,
        )
    }

    const runResendLetter = async (caseRef: string) => {
        setBusyClaim({ caseRef, action: 'resend' })
        try {
            const { data } = await api<SingleActionResponse>({
                url: '/actions/cancel',
                data: {
                    case_ref: caseRef,
                    introducer: origin,
                    action: 'resend-cancellation-letter',
                    source_surface: 'client-search',
                },
            })
            if (!data?.success) {
                Notiflix.Report.failure(
                    'Letter Not Sent',
                    data?.message || 'The cancellation letter could not be sent.',
                    'OK',
                )
                return
            }
            Notiflix.Report.success(
                'Letter Sent',
                `The cancellation letter for ${caseRef} was sent${data.recipient ? ` to ${data.recipient}` : ''}.`,
                'OK',
            )
        } catch (error: any) {
            Notiflix.Report.failure(
                'Letter Not Sent',
                errorMessage(error, 'The cancellation letter could not be sent.'),
                'OK',
            )
        } finally {
            setBusyClaim(null)
        }
    }

    const confirmResendLetter = (claim: ClaimState) => {
        Confirm.show(
            `Resend cancellation letter for ${claim.case_ref}?`,
            `The client${displayEmail ? ` (${displayEmail})` : ''} will receive the cancellation letter again. Nothing else changes.`,
            'Send letter',
            'Go back',
            () => void runResendLetter(claim.case_ref),
            () => undefined,
        )
    }

    const fetchDeletePreview = async () => {
        setIsPreviewing(true)
        setConfirmText('')
        try {
            const { data } = await api<DeletePreview>({
                url: '/client-search-delete-client',
                data: {
                    introducer: origin,
                    clientId,
                    preview: true,
                    allIntroducers: deleteAllIntroducers,
                    includeFunding: deleteFunding,
                },
            })
            if (!data?.success) {
                Notiflix.Report.failure(
                    'Preview Failed',
                    data?.message || 'Could not build the deletion preview.',
                    'OK',
                )
                return
            }
            setDeletePreview(data)
        } catch (error: any) {
            Notiflix.Report.failure(
                'Preview Failed',
                errorMessage(error, 'Could not build the deletion preview.'),
                'OK',
            )
        } finally {
            setIsPreviewing(false)
        }
    }

    const runDeletion = async () => {
        setIsDeleting(true)
        try {
            const { data } = await api<DeletePreview>({
                url: '/client-search-delete-client',
                data: {
                    introducer: origin,
                    clientId,
                    confirm: DELETE_CONFIRM_WORD,
                    allIntroducers: deleteAllIntroducers,
                    includeFunding: deleteFunding,
                },
            })
            if (!data?.success) {
                Notiflix.Report.failure('Deletion Failed', data?.message || 'Client deletion failed.', 'OK')
                return
            }
            Notiflix.Report.success(
                'Client Deleted',
                data.message || 'The client and their data have been deleted.',
                'OK',
                () => onClientDeleted?.(),
            )
        } catch (error: any) {
            Notiflix.Report.failure('Deletion Failed', errorMessage(error, 'Client deletion failed.'), 'OK')
        } finally {
            setIsDeleting(false)
        }
    }

    const confirmDeletion = () => {
        if (!deletePreview || confirmText.trim() !== DELETE_CONFIRM_WORD) return
        Confirm.show(
            'Permanently delete this client?',
            `${plural(deletePreview.totalClients, 'client record')} and ${plural(deletePreview.totalCases, 'case')} across ` +
                `${plural(deletePreview.introducers.length, 'database')} plus their documents will be erased. This cannot be undone.`,
            'Delete permanently',
            'Go back',
            () => void runDeletion(),
            () => undefined,
            notiflixDanger,
        )
    }

    // ------------------------------------------------------------------ render

    if (isLoading) {
        return <Spinner block text="Loading account actions…" />
    }

    if (loadError) {
        return (
            <EmptyState
                tone="error"
                title="Could not load account actions"
                description={loadError}
                action={
                    <Button size="sm" onClick={loadSummary}>
                        Retry
                    </Button>
                }
            />
        )
    }

    if (!summary || !summary.supported) {
        return (
            <Banner tone="warn" title="Not available for this database">
                {summary?.message || `Cancellation is not available for ${origin} from the dashboard.`}
            </Banner>
        )
    }

    const canPreviewDelete = !isPreviewing && !isDeleting
    const confirmMatches = confirmText.trim() === DELETE_CONFIRM_WORD
    const allActiveSelected = activeClaims.length > 0 && selectedRefs.size === activeClaims.length

    const claimColumns: Column<ClaimState>[] = [
        {
            key: 'select',
            header: (
                <Checkbox
                    label=""
                    aria-label="Select all active claims"
                    checked={allActiveSelected}
                    disabled={activeClaims.length === 0 || isCancelling}
                    onChange={(e) => (e.target.checked ? selectAllActive() : clearSelection())}
                />
            ),
            width: 36,
            render: (claim) => (
                <Checkbox
                    label=""
                    aria-label={`Select ${claim.case_ref}`}
                    checked={selectedRefs.has(claim.case_ref)}
                    disabled={claim.cancelled || isCancelling}
                    onChange={() => toggleRef(claim.case_ref)}
                />
            ),
        },
        {
            key: 'case_ref',
            header: 'Case ref',
            nowrap: true,
            render: (c) => <span className={styles.mono}>{c.case_ref}</span>,
        },
        { key: 'lender', header: 'Lender', render: (c) => c.lender || '—' },
        {
            key: 'policy_number',
            header: 'Agreement',
            render: (c) =>
                c.policy_number ? (
                    <span className={styles.mono}>{c.policy_number}</span>
                ) : (
                    <span className={styles.muted}>Pending discovery</span>
                ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (c) =>
                c.cancelled ? (
                    <span className={styles.statusCell}>
                        <StatusPill tone="bad" dot>
                            Cancelled{c.cancellation_date ? ` · ${formatDate(String(c.cancellation_date))}` : ''}
                        </StatusPill>
                        {c.cancellation_reason && <span className={styles.subtle}>{c.cancellation_reason}</span>}
                    </span>
                ) : c.has_state ? (
                    <StatusPill tone="good" dot>
                        Active
                    </StatusPill>
                ) : (
                    <StatusPill
                        tone="warn"
                        dot
                        title="No case-state row exists for this case yet. Cancelling creates one."
                    >
                        No case state
                    </StatusPill>
                ),
        },
        {
            key: 'actions',
            header: '',
            align: 'right',
            nowrap: true,
            render: (c) => {
                if (!c.cancelled) return null
                const busy = busyClaim?.caseRef === c.case_ref
                return (
                    <span className={styles.buttonRow}>
                        {capabilities.reactivate && (
                            <Button
                                size="sm"
                                variant="neutral"
                                disabled={Boolean(busyClaim) || isCancelling}
                                loading={busy && busyClaim?.action === 'reactivate'}
                                onClick={() => confirmReactivation(c)}
                            >
                                Reactivate
                            </Button>
                        )}
                        {capabilities.cancelWithLetter && (
                            <Button
                                size="sm"
                                variant="ghost"
                                disabled={Boolean(busyClaim) || isCancelling}
                                loading={busy && busyClaim?.action === 'resend'}
                                onClick={() => confirmResendLetter(c)}
                            >
                                Resend letter
                            </Button>
                        )}
                    </span>
                )
            },
        },
        {
            key: 'funding',
            header: 'Funding',
            render: (c) => {
                const linked = fundingByCaseRef.get(c.case_ref) || []
                if (!linked.length) return '—'
                return (
                    <span className={styles.pillRow}>
                        {linked.map((ref) => {
                            const status = fundingStatus(ref)
                            return (
                                <StatusPill
                                    key={`${ref.batch_group}-${ref.funder_case_ref}`}
                                    tone={status.tone}
                                    size="sm"
                                    title={`${ref.funder_case_ref} · ${ref.batch_group || 'no batch'}`}
                                >
                                    {ref.batch_group || ref.funder_case_ref}: {status.label}
                                </StatusPill>
                            )
                        })}
                    </span>
                )
            },
        },
    ]

    const fundingColumns: Column<FundingReference>[] = [
        {
            key: 'batch',
            header: 'Batch',
            render: (ref) => (
                <>
                    {ref.batch_group || '—'}
                    {ref.nera_id && <span className={styles.subtle}> · NERA {ref.nera_id}</span>}
                </>
            ),
        },
        {
            key: 'funder_case_ref',
            header: 'Funder case ref',
            nowrap: true,
            render: (ref) => <span className={styles.mono}>{ref.funder_case_ref}</span>,
        },
        {
            key: 'introducer_case_ref',
            header: 'Introducer case ref',
            nowrap: true,
            render: (ref) =>
                ref.introducer_case_ref ? <span className={styles.mono}>{ref.introducer_case_ref}</span> : '—',
        },
        {
            key: 'status',
            header: 'Status',
            render: (ref) => {
                const status = fundingStatus(ref)
                const note = ref.cancelled ? ref.cancelled_reason : ref.audit_removed ? ref.audit_removed_reason : null
                return (
                    <span className={styles.statusCell}>
                        <StatusPill tone={status.tone} dot>
                            {status.label}
                        </StatusPill>
                        {note && <span className={styles.subtle}>{note}</span>}
                    </span>
                )
            },
        },
        { key: 'client_status', header: 'Claim status', render: (ref) => ref.client_status || '—' },
        {
            key: 'matched',
            header: 'Matched by',
            nowrap: true,
            render: (ref) => (ref.direct_match ? `${ref.introducer} client` : `email (${ref.introducer})`),
        },
    ]

    return (
        <div className={styles.container}>
            <Banner tone="info">
                <strong>{summary.introducerLabel}</strong> client #{summary.client.id}
                {displayName ? ` — ${displayName}` : ''}
                {displayEmail ? ` (${displayEmail})` : ''}. Every action is recorded in the cancellation log with your
                email. Letters and emails are only sent when you choose to send them.
            </Banner>

            {/* Cancel claims ------------------------------------------------ */}
            <Card
                title="Cancel claims"
                subtitle="Tick the claims to cancel, or cancel every active claim at once. The reason is stored against each case and in the cancellation log. Cancelled claims can be reactivated from this table."
                padding="none"
                actions={
                    <span className={styles.pillRow}>
                        <StatusPill round tone={activeClaims.length ? 'accent' : 'neutral'}>
                            {plural(activeClaims.length, 'active', 'active')}
                        </StatusPill>
                        {cancelledClaims.length > 0 && (
                            <StatusPill round tone="bad">
                                {plural(cancelledClaims.length, 'cancelled', 'cancelled')}
                            </StatusPill>
                        )}
                    </span>
                }
            >
                <DataTable
                    bare
                    dense
                    columns={claimColumns}
                    rows={claims}
                    rowKey={(c) => c.case_ref}
                    rowTone={(c) => (c.cancelled ? 'bad' : undefined)}
                    emptyTitle="No claims"
                    emptyDescription="This client has no claims to cancel."
                />
                {activeClaims.length > 0 && (
                    <div className={styles.inset}>
                        <div className={styles.cancelControls}>
                            <Field label="What should happen">
                                <div className={styles.optionList} role="radiogroup" aria-label="Cancellation mode">
                                    <Radio
                                        name="cancel-mode"
                                        value="letter"
                                        checked={cancelMode === 'letter'}
                                        disabled={isCancelling || !capabilities.cancelWithLetter}
                                        onChange={() => setCancelMode('letter')}
                                        label="Cancel and send the client the cancellation letter"
                                        hint={
                                            capabilities.cancelWithLetter
                                                ? `One letter per claim${displayEmail ? `, to ${displayEmail}` : ''}. If a letter fails the claim still cancels and you can resend it.`
                                                : 'Letters are not configured for this introducer.'
                                        }
                                    />
                                    <Radio
                                        name="cancel-mode"
                                        value="silent"
                                        checked={cancelMode === 'silent'}
                                        disabled={isCancelling}
                                        onChange={() => setCancelMode('silent')}
                                        label="Cancel in the backend only (no letter or email)"
                                        hint="Use when the client has already been told, or for bookkeeping."
                                    />
                                </div>
                            </Field>
                            <FieldGrid minColumn={220}>
                                <Field
                                    label="Reason"
                                    hint={
                                        summary?.schemaFamily === 'oa'
                                            ? 'OA cases have no reason column; the reason is kept in the cancellation log only (max 45 characters)'
                                            : 'Stored against each cancelled case and in the cancellation log (max 45 characters)'
                                    }
                                >
                                    {(id) => (
                                        <Input
                                            id={id}
                                            maxLength={45}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder={DEFAULT_REASON}
                                            disabled={isCancelling}
                                        />
                                    )}
                                </Field>
                                {fundingRefs.length > 0 && (
                                    <Field label="Funding data">
                                        <Checkbox
                                            checked={cancelInFunding}
                                            disabled={isCancelling}
                                            onChange={(e) => setCancelInFunding(e.target.checked)}
                                            label="Also mark as cancelled in funding data"
                                            hint={
                                                selectedRefs.size > 0
                                                    ? `${plural(selectedFundingRefs, 'linked reference')} affected`
                                                    : `${plural(fundingRefs.length, 'reference')} linked to this client`
                                            }
                                        />
                                    </Field>
                                )}
                            </FieldGrid>
                            <div className={styles.buttonRow}>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={selectedRefs.size === 0 || isCancelling}
                                    loading={isCancelling && selectedRefs.size > 0}
                                    onClick={() => confirmCancellation(false)}
                                >
                                    {selectedRefs.size
                                        ? `Cancel ${plural(selectedRefs.size, 'selected claim')}`
                                        : 'Cancel selected claims'}
                                    {sendLetter ? ' + letter' : ''}
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={isCancelling}
                                    onClick={() => confirmCancellation(true)}
                                >
                                    Cancel all {plural(activeClaims.length, 'active claim')}
                                    {sendLetter ? ' + letters' : ''}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                {cancelledClaims.length > 0 && capabilities.reactivate && (
                    <div className={styles.inset}>
                        <Checkbox
                            checked={resumedEmail}
                            disabled={Boolean(busyClaim)}
                            onChange={(e) => setResumedEmail(e.target.checked)}
                            label="When reactivating, send the client a “claim resumed” email"
                            hint={`Applies to the Reactivate buttons above${displayEmail ? ` (${displayEmail})` : ''}.`}
                        />
                    </div>
                )}
                {cancelResult && (
                    <div className={styles.inset}>
                        <Banner
                            tone={cancelResult.success && !(cancelResult.letterFailures?.length ?? 0) ? 'good' : 'warn'}
                        >
                            {cancelResult.message}
                        </Banner>
                    </div>
                )}
                {reactivateResult && (
                    <div className={styles.inset}>
                        <Banner tone="good">{reactivateResult}</Banner>
                    </div>
                )}
            </Card>

            {/* Funding data ----------------------------------------------------- */}
            <Card
                title="Funding data"
                subtitle="Funding-monitor references linked to this client by case reference or email"
                padding="none"
                actions={<StatusPill round>{fundingRefs.length}</StatusPill>}
            >
                {!summary.funding.available ? (
                    <div className={styles.inset}>
                        <Banner tone="warn" title="Could not read funding-monitor">
                            {summary.funding.error || 'Unknown error'}
                        </Banner>
                    </div>
                ) : (
                    <DataTable
                        bare
                        dense
                        columns={fundingColumns}
                        rows={fundingRefs}
                        rowKey={(ref) => `${ref.funder_client_id}-${ref.batch_group}-${ref.funder_case_ref}`}
                        rowTone={(ref) => (fundingStatus(ref).tone === 'good' ? undefined : fundingStatus(ref).tone)}
                        emptyTitle="No funding records"
                        emptyDescription="No funding-monitor records are linked to this client."
                    />
                )}
            </Card>

            {/* Delete client ------------------------------------------------------ */}
            {!capabilities.delete ? (
                <Card tone="bad" title="Delete client and all data">
                    <Banner tone="neutral">
                        Full data deletion is not available for {summary.introducerLabel} from the dashboard: the
                        deletion tool only understands the standard introducer (leads) schema.
                    </Banner>
                </Card>
            ) : (
                <Card
                    tone="bad"
                    title="Delete client and all data"
                    subtitle={
                        <>
                            Removes the client, every linked record (cases, signatures, credit reports, uploads, webhook
                            archives) and all documents in S3 for this <strong>{summary.introducerLabel}</strong>{' '}
                            record. Preview first — the deletion is permanent.
                        </>
                    }
                    footer={
                        <div className={styles.buttonRow}>
                            <Button
                                size="sm"
                                disabled={!canPreviewDelete}
                                loading={isPreviewing}
                                onClick={() => void fetchDeletePreview()}
                            >
                                {deletePreview ? 'Refresh preview' : 'Preview deletion'}
                            </Button>
                        </div>
                    }
                >
                    <div className={styles.stack}>
                        <div className={styles.optionList}>
                            <Checkbox
                                checked={deleteAllIntroducers}
                                disabled={isPreviewing || isDeleting || !displayEmail}
                                onChange={(e) => {
                                    setDeleteAllIntroducers(e.target.checked)
                                    resetDeletePreview()
                                }}
                                label="Also delete this person from every other introducer database (matched by email)"
                                hint={
                                    summary.otherIntroducers.length > 0
                                        ? `Found in ${summary.otherIntroducers
                                              .map((m) =>
                                                  m.error ? `${m.label} (lookup failed)` : `${m.label} (${m.clients})`,
                                              )
                                              .join(', ')}`
                                        : 'No other introducer records found for this email'
                                }
                            />
                            <Checkbox
                                checked={deleteFunding}
                                disabled={isPreviewing || isDeleting}
                                onChange={(e) => {
                                    setDeleteFunding(e.target.checked)
                                    resetDeletePreview()
                                }}
                                label="Also remove funding-monitor records"
                                hint={
                                    fundingRefs.length > 0
                                        ? `${plural(fundingRefs.length, 'reference')} linked`
                                        : 'None currently linked'
                                }
                            />
                        </div>

                        {deletePreview && (
                            <Card muted padding="sm" title="Deletion preview">
                                <div className={styles.stack}>
                                    <p className={styles.previewHeadline}>
                                        <strong>{plural(deletePreview.totalClients, 'client record')}</strong> and{' '}
                                        <strong>{plural(deletePreview.totalCases, 'case')}</strong> across{' '}
                                        <strong>{plural(deletePreview.introducers.length, 'database')}</strong> will be
                                        erased.
                                    </p>

                                    {deletePreview.introducers.map((intro) => (
                                        <div key={intro.introducer} className={styles.previewBlock}>
                                            <p className={styles.previewSub}>
                                                <strong>{intro.label}</strong>: {plural(intro.clients, 'client')},{' '}
                                                {plural(intro.cases, 'case')},{' '}
                                                {plural(intro.s3PrefixCount, 'S3 folder')} +{' '}
                                                {plural(intro.s3KeyCount, 'file')}
                                            </p>
                                            <KeyValueList
                                                columns
                                                items={Object.entries(intro.tableCounts)
                                                    .filter(([, n]) => n > 0)
                                                    .map(([table, n]) => ({ label: table, value: n, mono: true }))}
                                            />
                                        </div>
                                    ))}

                                    <p className={styles.previewSub}>
                                        {deletePreview.funding.skipped ? (
                                            'Funding-monitor records will be left in place.'
                                        ) : (
                                            <>
                                                Funding-monitor:{' '}
                                                {plural(deletePreview.funding.counts.funder_clients, 'client')},{' '}
                                                {plural(deletePreview.funding.counts.funder_references, 'reference')},{' '}
                                                {plural(deletePreview.funding.counts.funder_claims, 'claim')}
                                                {deletePreview.funding.batchGroups.length
                                                    ? ` (batches: ${deletePreview.funding.batchGroups.join(', ')})`
                                                    : ''}{' '}
                                                will be deleted.
                                            </>
                                        )}
                                    </p>

                                    {!deletePreview.funding.skipped &&
                                        deletePreview.funding.counts.funder_references > 0 && (
                                            <Banner tone="warn">
                                                These funding rows belong to batches already shared with funders.
                                                Deleting them removes the claims from batch views and exports.
                                            </Banner>
                                        )}

                                    <div className={styles.confirmRow}>
                                        <Field
                                            label={`Type ${DELETE_CONFIRM_WORD} to confirm`}
                                            className={styles.confirmField}
                                        >
                                            {(id) => (
                                                <Input
                                                    id={id}
                                                    value={confirmText}
                                                    onChange={(e) => setConfirmText(e.target.value)}
                                                    disabled={isDeleting}
                                                    autoComplete="off"
                                                    mono
                                                    invalid={confirmText.length > 0 && !confirmMatches}
                                                />
                                            )}
                                        </Field>
                                        <Button
                                            variant="danger"
                                            icon={<FaTrash />}
                                            disabled={!confirmMatches || isDeleting}
                                            loading={isDeleting}
                                            onClick={confirmDeletion}
                                        >
                                            Delete client permanently
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        )}
                    </div>
                </Card>
            )}
        </div>
    )
}
